"use client"

import {
  BarChart3,
  BookOpen,
  Cog,
  FolderGit2,
  Hammer,
  LogOut,
  Megaphone,
  Server,
  Stethoscope,
  Users,
  type LucideIcon,
} from "lucide-react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useQueryClient } from "@tanstack/react-query"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@packages/web/components/ui/sidebar"
import { cn } from "@packages/web/components/ui/utils"

import { backofficeSidebarFooterDockButton } from "@backoffice-web/components/backoffice-ui"

import BackofficeBrand from "@/components/BackofficeBrand"
import useGetAuthentication from "@packages/base/hooks/use-get-authentication"
import useSignout from "@packages/base/hooks/use-signout"

type NavItem = {
  Icon: LucideIcon
  label: string
  to: string
  /** Match any route under this prefix (defaults to `to`). */
  activePrefix?: string
}

const editorNavItems: NavItem[] = [
  { Icon: BarChart3, label: "Analytics", to: "/dashboard/analytics" },
  { Icon: Users, label: "Inscriptions", to: "/dashboard/inscriptions" },
  { Icon: Megaphone, label: "Communications", to: "/dashboard/communications" },
  { Icon: Hammer, label: "Crafts", to: "/dashboard/crafts" },
  { Icon: Cog, label: "Opérateurs", to: "/dashboard/operators" },
  {
    Icon: Stethoscope,
    label: "Audits",
    to: "/dashboard/audits/monitoring",
    activePrefix: "/dashboard/audits",
  },
  { Icon: BookOpen, label: "Guidelines", to: "/dashboard/guidelines" },
  { Icon: FolderGit2, label: "Worktrees", to: "/dashboard/worktrees" },
]

const developerModeNavItem: NavItem = {
  Icon: Server,
  label: "Mode développeur",
  to: "/dashboard/dev",
}

function navMatchPrefix(item: NavItem): string {
  return item.activePrefix ?? item.to
}

function activeNavTo(pathname: string): string | null {
  const matches = [...editorNavItems, developerModeNavItem]
    .filter((item) => {
      const prefix = navMatchPrefix(item)
      return pathname === prefix || pathname.startsWith(`${prefix}/`)
    })
    .sort((a, b) => navMatchPrefix(b).length - navMatchPrefix(a).length)
  return matches[0]?.to ?? null
}

export default function AppSidebar() {
  const pathname = usePathname()
  const activeTo = activeNavTo(pathname)
  const queryClient = useQueryClient()
  const { isMobile, setOpenMobile } = useSidebar()
  const { data: authentication } = useGetAuthentication(
    { redirectIfNotAuthenticated: false },
    queryClient,
  )
  const { isPending: isSignoutPending, mutate: signout } = useSignout({}, queryClient)

  const closeMobile = () => {
    if (isMobile) setOpenMobile(false)
  }

  const DeveloperModeIcon = developerModeNavItem.Icon
  const developerModeActive = activeTo === developerModeNavItem.to

  if (!authentication?.isEditor) return null

  return (
    <Sidebar collapsible="offcanvas" className="border-r border-border/60 bg-white">
      {/* Brand lives in the fixed MobileHeader on small screens; keep it here for desktop only. */}
      {!isMobile && (
        <SidebarHeader className="px-3 py-4">
          <Link
            href="/"
            className="rounded-md outline-none ring-sidebar-ring focus-visible:ring-2"
            onClick={closeMobile}
            aria-label="Ernest backoffice — accueil"
          >
            <BackofficeBrand iconSize={32} />
          </Link>
        </SidebarHeader>
      )}

      <SidebarContent>
        <SidebarGroup className={isMobile ? "pt-2" : "pt-0"}>
          <SidebarGroupContent>
            <SidebarMenu className="gap-1 px-1">
              {editorNavItems.map(({ Icon, label, to }) => {
                const active = activeTo === to
                return (
                  <SidebarMenuItem key={to}>
                    <SidebarMenuButton
                      asChild
                      isActive={active}
                      tooltip={label}
                      className={cn(
                        "h-11 rounded-xl px-3 transition-colors",
                        active
                          ? "bg-nav-active font-bold text-ink hover:bg-nav-active hover:text-ink data-[active=true]:bg-nav-active data-[active=true]:text-ink"
                          : "font-medium text-nav-muted hover:bg-[#f2f2f7] hover:text-ink",
                      )}
                    >
                      <Link href={to} onClick={closeMobile} aria-current={active ? "page" : undefined}>
                        <Icon
                          className={cn("size-5", active ? "text-ink" : "text-nav-muted")}
                          strokeWidth={active ? 2.25 : 1.75}
                        />
                        <span>{label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-border/40 p-0">
        <SidebarMenu className="gap-1 px-1 py-2">
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={developerModeActive}
              tooltip={developerModeNavItem.label}
              className={cn(
                "h-11 rounded-xl px-3 transition-colors",
                developerModeActive
                  ? "bg-nav-active font-bold text-ink hover:bg-nav-active hover:text-ink data-[active=true]:bg-nav-active data-[active=true]:text-ink"
                  : "font-medium text-nav-muted hover:bg-[#f2f2f7] hover:text-ink",
              )}
            >
              <Link
                href={developerModeNavItem.to}
                onClick={closeMobile}
                aria-current={developerModeActive ? "page" : undefined}
              >
                <DeveloperModeIcon
                  className={cn("size-5", developerModeActive ? "text-ink" : "text-nav-muted")}
                  strokeWidth={developerModeActive ? 2.25 : 1.75}
                />
                <span>{developerModeNavItem.label}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              disabled={isSignoutPending}
              tooltip="Se déconnecter"
              className={cn(
                backofficeSidebarFooterDockButton,
                "px-3 font-medium text-nav-muted hover:bg-[#f2f2f7] hover:text-ink",
              )}
              onClick={() => signout()}
            >
              <LogOut />
              <span>{isSignoutPending ? "Déconnexion..." : "Se déconnecter"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
